import React, { useEffect, useState } from "react";
import { teamImg } from "../../assets";
import { useLanguage } from "../../contexts/LanguageContext";
import { GetAxios } from "../../helpers/axiosHelpers";
import { useContentBlocks } from "../../hooks/useContentBlocks";
import { pickBlock } from "../../helpers/contentHelpers";
import { resolveIcon } from "../../helpers/iconResolver";

const DefaultStepIcon = () => (
  <svg width="48" height="48" viewBox="0 0 64 64" fill="none" xmlns="http://www.w3.org/2000/svg">
    <ellipse cx="44.2783" cy="16.5243" rx="19.2783" ry="8.26215" stroke="#367FAC" strokeWidth="2.3"/>
    <path d="M25 24.7866C25 24.7866 25 31.2398 25 35.8028C25 40.3659 33.6317 44.065 44.2785 44.065C54.9255 44.065 63.5575 40.3659 63.5575 35.8028C63.5575 33.5252 63.5575 24.7866 63.5575 24.7866" stroke="#367FAC" strokeWidth="2.3" strokeLinecap="square"/>
    <path d="M25 8.26215C25 8.26215 25 17.4694 25 22.0324C25 26.5955 33.6317 30.2946 44.2785 30.2946C54.9255 30.2946 63.5575 26.5955 63.5575 22.0324C63.5575 19.7548 63.5575 8.26215 63.5575 8.26215" stroke="#367FAC" strokeWidth="2.3"/>
  </svg>
);

const MiscSection = ({ sectionData }) => {
  const { t, language } = useLanguage();
  const pick = (en, ja, key) => (language === "ja" && ja) ? ja : (en ?? t(key));
  const [cards, setCards] = useState([]);

  // Fetch content blocks for team stats
  const sectionId = sectionData?.id;
  const { blocks: teamBlocks } = useContentBlocks(sectionId, "our-services", "stat");

  useEffect(() => {
    GetAxios("/service-cards/public?section=mission")
      .then((res) => {
        if (res?.success && Array.isArray(res.data) && res.data.length > 0) {
          setCards(res.data);
        }
      })
      .catch(() => {});
  }, []);

  // Locale fallback for the three mission pillars
  const localeSteps = [
    { titleKey: "mission.jobPlacement.title", descKey: "mission.jobPlacement.description" },
    { titleKey: "mission.employerSupport.title", descKey: "mission.employerSupport.description" },
    { titleKey: "mission.careerGuidance.title", descKey: "mission.careerGuidance.description" },
  ];

  const serviceSteps = cards.length > 0
    ? cards.map((c, i) => ({
        num: String(i + 1).padStart(2, "0"),
        title: (language === "ja" && c.titleJa) ? c.titleJa : c.titleEn,
        desc: (language === "ja" && c.descJa) ? c.descJa : c.descEn,
        iconName: c.iconName,
      }))
    : localeSteps.map((s, i) => ({
        num: String(i + 1).padStart(2, "0"),
        title: t(s.titleKey),
        desc: t(s.descKey),
      }));

  // Build team stats from content blocks or fallback
  const teamStats = teamBlocks.length > 0
    ? teamBlocks.slice(0, 2).map((block) => {
        const picked = pickBlock(block, language);
        return { value: picked.value, label: picked.label, description: picked.description };
      })
    : [
        {
          value: t("mission.team.experience.value"),
          label: t("mission.team.experience.label"),
          description: t("mission.team.experience.description"),
        },
        {
          value: t("mission.team.expertise.value"),
          label: t("mission.team.expertise.label"),
          description: t("mission.team.expertise.description"),
        },
      ];

  return (
    <>
      <section className="py-14 bg-[#292F3A]" id="mission">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="flex flex-col lg:flex-row items-start gap-8 mb-10">
            <div className="lg:w-1/2">
              <div className="flex items-center gap-2 mb-3">
                <div className="w-1 h-4 bg-accent-green rounded-full"></div>
                <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">
                  {pick(sectionData?.labelEn, sectionData?.labelJa, "mission.sectionLabel")}
                </span>
              </div>
              <h2 className="text-white text-3xl font-semibold leading-tight tracking-tight">
                {pick(sectionData?.titleEn, sectionData?.titleJa, "mission.title")}
              </h2>
            </div>
            <p className="text-[#C7C7C7] text-base leading-relaxed max-w-xl lg:mt-7">
              {pick(sectionData?.subtitleEn, sectionData?.subtitleJa, "mission.subtitle")}
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
            {serviceSteps.map((step) => {
              const IconComp = step.iconName ? resolveIcon(step.iconName) : null;

              return (
                <div key={step.num} className="bg-white rounded-[16px] p-6 flex flex-col hover:shadow-xl transition-all duration-300 hover:-translate-y-1">
                  <div className="flex items-center justify-between mb-5">
                    {IconComp ? (
                      <div className="w-12 h-12 bg-[#367FAC]/10 rounded-xl flex items-center justify-center">
                        <IconComp className="w-6 h-6 text-[#367FAC]" />
                      </div>
                    ) : (
                      <DefaultStepIcon />
                    )}
                    <span className="text-[11px] font-black text-slate-400 bg-slate-100 rounded-full px-2.5 py-1 tracking-widest">
                      {step.num}
                    </span>
                  </div>
                  <h3 className="text-[#0D1317] text-xl font-semibold mb-2 tracking-tight">
                    {step.title}
                  </h3>
                  <p className="text-[#4A5D69] text-sm leading-relaxed flex-1">
                    {step.desc}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="py-14 bg-white">
        <div className="max-w-[1280px] mx-auto px-8">
          <div className="flex flex-col lg:flex-row items-center justify-between gap-10">
            <div className="max-w-xl">
              <div className="flex items-center gap-2 mb-4">
                <div className="w-1 h-4 bg-accent-green rounded-full"></div>
                <span className="text-[10px] font-black text-accent-green uppercase tracking-[0.25em]">
                  {t("mission.team.sectionLabel")}
                </span>
              </div>
              <h2 className="text-[#0D1317] text-4xl font-semibold leading-tight mb-8 tracking-tight">
                {t("mission.team.title")}
              </h2>

              <div className="flex flex-col sm:flex-row gap-4">
                {teamStats.map((stat, i) => (
                  <div key={i} className="flex-1 border border-[#B4BBC5] rounded-[12px] p-5 hover:shadow-md transition-all duration-300 hover:border-[#367FAC]/40">
                    <p className="text-[#367FAC] text-2xl font-bold leading-tight mb-1.5">
                      {stat.value}
                    </p>
                    <p className="text-[#0D1317] text-base font-semibold mb-2 tracking-tight">
                      {stat.label}
                    </p>
                    <p className="text-[#4A5D69] text-sm leading-snug">
                      {stat.description}
                    </p>
                  </div>
                ))}
              </div>
            </div>

            <div>
              <img src={teamImg} alt="Team" className="rounded-[24px] w-full max-w-[540px] h-auto" loading="lazy" />
            </div>
          </div>
        </div>
      </section>
    </>
  );
};

export default MiscSection;
